import React, { useState, useEffect, useRef } from "react"
import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  XMarkIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline"
import Menu from "aldoo-ra/Menu"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Button from "aldoo-ra/Button"
import { GetCMSContent } from "aldoo-ra/CMS/api"

const PREVIEW_MODES = {
  DESKTOP: { name: "desktop", width: 1280 },
}

const ThemePreviewEditor = ({ theme, allThemes = [], onClose, onThemeChange }) => {
  const [previewMode, setPreviewMode] = useState(PREVIEW_MODES.DESKTOP)
  const [themes, setThemes] = useState(allThemes)
  const [isLoading, setIsLoading] = useState(false)
  const dialogRef = useRef(null)
  const iframeRef = useRef(null)
  
  // Transform initial theme to expected format
  const [currentTheme, setCurrentTheme] = useState(() => {
    if (!theme) return null
    
    
    // If theme comes from CMS, transform it
    if (theme.content) {
      const transformed = {
        ...theme.content,
        _id: theme._id || theme.id
      }
      return transformed
    }
    // If already transformed, use as is
    return {
      ...theme,
      _id: theme._id || theme.id
    }
  })

  // Send theme changes via postMessage instead of reloading iframe
  React.useEffect(() => {
    
    // Send theme change via postMessage (no iframe reload)
    if (currentTheme && iframeRef.current) {
      sendThemeChangeMessage(currentTheme)
    }
  }, [currentTheme])

  // Load themes if not provided
  useEffect(() => {
    const loadThemes = async () => {
      if (!themes.length) {
        setIsLoading(true)
        try {
          const response = await GetCMSContent({
            match: {
              "content.type": "SiteTheme",
              deleted: { $ne: true }
            }
          })
          if (response.data) {
            setThemes(response.data)
          }
        } catch (error) {
          console.error('Failed to load themes:', error)
        }
        setIsLoading(false)
      }
    }
    loadThemes()
  }, [themes.length])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose()
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [onClose])

  // Commented out click-outside detection to prevent conflicts with dropdown
  // Users can close via Escape key or X button
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (dialogRef.current && !dialogRef.current.contains(event.target)) {
  //       onClose()
  //     }
  //   }
  //   
  //   document.addEventListener("mousedown", handleClickOutside)
  //   return () => document.removeEventListener("mousedown", handleClickOutside)
  // }, [onClose])

  // Send theme change when currentTheme changes (including initial load)
  useEffect(() => {
    if (!currentTheme) return

    // Wait a bit to ensure iframe is loaded before sending message
    const timeout = setTimeout(() => {
      sendThemeChangeMessage(currentTheme)
    }, 100)

    return () => clearTimeout(timeout)
  }, [currentTheme])


  // Send theme change message to iframe
  const sendThemeChangeMessage = (theme) => {
    if (!iframeRef.current || !iframeRef.current.contentWindow) {
      return
    }

    try {
      iframeRef.current.contentWindow.postMessage({
        type: 'THEME_PREVIEW_CHANGE',
        theme: theme
      }, window.location.origin)
    } catch (error) {
      console.warn('ThemePreviewEditor: failed to send theme message', error)
    }
  }

  const getPreviewTitle = () => {
    const themeName = currentTheme?.name || "Theme"
    if (previewMode.name === "desktop") {
      return `${themeName} - Desktop Preview`
    }
    return `${themeName} - ${previewMode.label} (${previewMode.width}x${previewMode.height})`
  }

  const handleThemeChange = (selectedThemes) => {
    if (selectedThemes.length > 0) {
      const selectedTheme = themes.find(t => t._id === selectedThemes[0].value)
      if (selectedTheme) {
        // Transform theme to the format expected by SiteTheme system
        // CMS themes have data nested under 'content', but SiteTheme expects it at root level
        const transformedTheme = {
          ...selectedTheme.content,
          _id: selectedTheme._id
        }
        
        setCurrentTheme(transformedTheme)
        if (onThemeChange) {
          onThemeChange(transformedTheme)
        }
      }
    }
  }

  const refreshPreview = () => {
    if (iframeRef.current) {
      // Force a complete reload of the iframe
      iframeRef.current.src = iframeRef.current.src
      
      // Re-send current theme after a short delay
      setTimeout(() => {
        if (currentTheme) {
          sendThemeChangeMessage(currentTheme)
        }
      }, 500)
    }
  }

  // Prepare theme options for dropdown
  const themeOptions = themes.map(theme => ({
    value: theme._id,
    name: theme.content?.name || theme.name || 'Unnamed Theme'
  }))

  const currentThemeOption = currentTheme ? [{
    value: currentTheme._id || currentTheme.id,
    name: currentTheme.name || 'Current Theme'
  }] : []

  const render = (item) => {
    return (
      <div className="flex flex-row justify-between items-center gap-2 w-full p-2 hover:bg-admin_neutral_1/50">
        <div className="flex items-center gap-2">
          {item.icon}
          <span>{item.label}</span>
        </div>
        {item.options?.device && (
          <span className="text-sm text-admin_neutral_2">
            {item.options.device.width}x{item.options.device.height}
          </span>
        )}
      </div>
    )
  }

  const menuData = {
    label: "Mobile Devices",
    render,
    icon: <DevicePhoneMobileIcon className="w-5 h-5" />,
    items: [
      {
        label: "Phones",
        render,
        items: [
          {
            label: "iPhone 14",
            render,
            options: {
              action: "selectDevice",
              device: { label: "iPhone 14", width: 390, height: 844 },
            },
          },
          {
            label: "iPhone 14 Pro Max",
            render,
            options: {
              action: "selectDevice",
              device: { label: "iPhone 14 Pro Max", width: 430, height: 932 },
            },
          },
          {
            label: "Pixel 7",
            render,
            options: {
              action: "selectDevice",
              device: { label: "Pixel 7", width: 412, height: 915 },
            },
          },
          {
            label: "Samsung S23",
            render,
            options: {
              action: "selectDevice",
              device: { label: "Samsung S23", width: 360, height: 780 },
            },
          },
        ],
      },
      {
        label: "Tablets",
        render,
        items: [
          {
            label: "iPad",
            render,
            options: {
              action: "selectDevice",
              device: { label: "iPad", width: 820, height: 1180 },
            },
          },
          {
            label: "iPad Pro",
            render,
            options: {
              action: "selectDevice",
              device: { label: "iPad Pro", width: 1024, height: 1366 },
            },
          },
          {
            label: "Samsung Tab S8",
            render,
            options: {
              action: "selectDevice",
              device: { label: "Samsung Tab S8", width: 1440, height: 900 },
            },
          },
        ],
      },
    ],
  }

  const actionMap = {
    selectDevice: ({ options }) => {
      setPreviewMode({ name: "mobile", ...options.device })
    },
  }

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center backdrop-blur-sm"
      style={{ zIndex: 1100 }}
    >
      <div
        ref={dialogRef}
        className="bg-gray-900 w-full h-full flex flex-col text-white"
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between p-4 border-b border-admin_bordered">
          {/* Left side - Theme selector */}
          <div className="flex items-center gap-4 min-w-[300px]">
            <span className="text-sm text-admin_neutral_2">Theme:</span>
            <DropdownSelect
              variant="admin"
              options={themeOptions}
              value={currentThemeOption}
              onChange={handleThemeChange}
              placeholder="Select theme"
              multiSelection={false}
              showCheckbox={false}
              showSelectionTick={true}
              enableResetButton={false}
              className="min-w-[200px]"
              disabled={isLoading}
            />
          </div>

          {/* Center - Title */}
          <h2 className="text-lg font-medium text-admin_neutral_1 text-center flex-1">
            {getPreviewTitle()}
          </h2>

          {/* Right side - controls */}
          <div className="flex items-center gap-4 min-w-[200px] justify-end">
            <button
              onClick={refreshPreview}
              className="p-2 rounded text-admin_neutral_2 hover:bg-gray-800 hover:text-white transition-colors"
              title="Refresh preview"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>

            <button
              onClick={() => setPreviewMode(PREVIEW_MODES.DESKTOP)}
              className={`p-2 rounded transition-colors ${
                previewMode.name === "desktop"
                  ? "bg-admin_neutral_3 text-white"
                  : "text-admin_neutral_2 hover:bg-gray-800 hover:text-white"
              }`}
              title="Desktop view"
            >
              <ComputerDesktopIcon className="w-5 h-5" />
            </button>

            <div className="relative">
              <Menu
                menuData={menuData}
                actionMap={actionMap}
                renderAs="vlist.menu"
                itemWidth="250px"
                submenuMode="vertical"
                menuAlign="right"
                rootItemRender={(item) => (
                  <div className="flex flex-row justify-between items-center gap-2 w-full p-2">
                    <div className="flex items-center gap-2">{item.icon}</div>
                  </div>
                )}
                menuItemRender={(item) => (
                  <div className="flex flex-row justify-between items-center gap-2 w-full p-2 hover:bg-admin_neutral_1/50">
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <span>{item.label}</span>
                    </div>
                    {item.options?.device && (
                      <span className="mr-5 text-sm text-admin_neutral_2">
                        {item.options.device.width}x{item.options.device.height}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded text-admin_neutral_2 hover:bg-gray-800 hover:text-white transition-colors"
              title="Close preview"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-hidden bg-gray-800 p-4">
          <div className="flex justify-center h-full">
            <div
              style={{
                width:
                  previewMode.name === "desktop"
                    ? "100%"
                    : `${previewMode.width}px`,
                maxWidth:
                  previewMode.name === "desktop"
                    ? "100%"
                    : `${previewMode.width}px`,
                height: "100%",
                minHeight: previewMode.name === "mobile" ? `${previewMode.height}px` : "100%",
              }}
              className="bg-white shadow-xl rounded-lg transition-all duration-300 overflow-hidden"
            >
              <iframe
                ref={iframeRef}
                src={`${window.location.origin}/`}
                className="w-full h-full border-0"
                title="Theme Preview"
                onLoad={() => {
                  // Send initial theme when iframe loads
                  if (currentTheme) {
                    // Wait longer for the SiteTheme system to fully initialize in the iframe
                    setTimeout(() => {
                      sendThemeChangeMessage(currentTheme)
                    }, 800) // Longer delay to ensure SiteTheme system is ready
                    
                    // Also try a second time in case the first one is too early
                    setTimeout(() => {
                      sendThemeChangeMessage(currentTheme)
                    }, 1500)
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ThemePreviewEditor